<template>
  <div class="product__cans-pack-selector__inner">
    <slot />
  </div>
</template>

<script>
import { mapState } from 'pinia'
import {
  CANS_SELECTORS_UPDATED_EVENT,
  initCansSiblingDelegation,
} from '../../helpers/cans-sibling-swap'
import {
  formatPerCanLine,
  getVariantSellingTotalForPackRow,
  normalizeSellingPlanId,
  parseSellingPlanAllocationsJson,
} from '../../helpers/cans-per-can-price'
import { productPrice } from '../../helpers/product'
import { useProductStore } from '../../pinia/ProductStore'

export default {
  name: 'CansPackSelector',

  computed: {
    ...mapState(useProductStore, [
      'combinedListingVariantByOptionValueKey',
      'product',
      'selectedSellingPlan',
    ]),
  },

  watch: {
    product: {
      deep: true,
      handler () {
        this.queuePackPerCanUpdate()
      },
    },
    selectedSellingPlan: {
      deep: true,
      handler () {
        this.queuePackPerCanUpdate()
      },
    },
  },

  mounted () {
    initCansSiblingDelegation()
    this.$el.addEventListener(
      CANS_SELECTORS_UPDATED_EVENT,
      this.queuePackPerCanUpdate
    )
    this.queuePackPerCanUpdate()
  },

  beforeUnmount () {
    this.$el.removeEventListener(
      CANS_SELECTORS_UPDATED_EVENT,
      this.queuePackPerCanUpdate
    )
  },

  methods: {
    queuePackPerCanUpdate () {
      this.$nextTick(() => this.updatePackPerCanLabels())
    },

    getVariantForPackRow (anchor, variants) {
      const resolvedVariantId = this.resolveCombinedVariantIdForPackRow(anchor)
      const fallbackVariantId = anchor?.getAttribute?.('data-variant-id')
      const variantId = resolvedVariantId || fallbackVariantId

      if (!variantId) return null

      return variants.find((v) => String(v?.id) === String(variantId)) || null
    },

    resolveCombinedVariantIdForPackRow (anchor) {
      const map = this.combinedListingVariantByOptionValueKey || {}
      if (!anchor || Object.keys(map).length === 0) return null

      const rowOptionValueId = anchor.dataset?.optionValueId
      const rowOptionPosition = parseInt(
        anchor.dataset?.optionPosition ||
          this.$el.getAttribute('data-option-position') ||
          '0',
        10
      )

      if (!rowOptionValueId || !(rowOptionPosition > 0)) return null

      const sectionEl = this.$el.closest('[data-cans-section]')
      if (!sectionEl?.querySelectorAll) return null

      const pairs = [
        {
          position: rowOptionPosition,
          id: String(rowOptionValueId),
        },
      ]

      sectionEl
        .querySelectorAll(
          'li[role="radio"].is-active a[data-option-value-id][data-option-position]'
        )
        .forEach((activeAnchor) => {
          const position = parseInt(
            activeAnchor.dataset?.optionPosition || '0',
            10
          )

          if (!(position > 0) || position === rowOptionPosition) return

          pairs.push({
            position,
            id: String(activeAnchor.dataset.optionValueId),
          })
        })

      pairs.sort((a, b) => a.position - b.position)
      return map[pairs.map((p) => p.id).join(',')] || null
    },

    getSelectedSellingPlanAllocationIndex (inner, plan) {
      if (!plan?.id) return -1

      const activeAnchor = inner.querySelector(
        '.product__cans-pack-selector__grid li.is-active a[data-variant-allocations]'
      )
      const allocations = parseSellingPlanAllocationsJson(
        activeAnchor?.getAttribute?.('data-variant-allocations')
      )
      const wantedPlanId = normalizeSellingPlanId(plan.id)

      return allocations.findIndex(
        (allocation) =>
          normalizeSellingPlanId(allocation?.selling_plan_id) === wantedPlanId
      )
    },

    updatePackPerCanLabels () {
      const inner = this.$el
      if (!inner?.querySelector) return

      const grid = inner.querySelector('.product__cans-pack-selector__grid')
      const templateRaw =
        grid?.getAttribute?.('data-per-can-template')?.trim?.() ?? ''

      const variants = Array.isArray(this.product?.variants)
        ? this.product.variants
        : []
      const plan = this.selectedSellingPlan
      const selectedSellingPlanIndex =
        this.getSelectedSellingPlanAllocationIndex(inner, plan)

      inner
        .querySelectorAll('.product__cans-pack-selector__grid li[data-pack-count]')
        .forEach((li) => {
          const packCount = parseInt(li.getAttribute('data-pack-count') || '0', 10)
          if (!(packCount > 0)) return

          const anchor = li.querySelector('a[data-variant-id]')
          if (!anchor) return

          const variantPiniaFallback = this.getVariantForPackRow(
            anchor,
            variants
          )
          const otpAttr =
            variantPiniaFallback?.price ??
            anchor.getAttribute?.('data-variant-otp-cents')
          const allocAttr = Array.isArray(
            variantPiniaFallback?.selling_plan_allocations
          )
            ? JSON.stringify(variantPiniaFallback.selling_plan_allocations)
            : anchor.getAttribute?.('data-variant-allocations')

          const row = li.querySelector('.product__cans-pack-selector__per-can')
          if (
            !row ||
            row.classList.contains(
              'product__cans-pack-selector__per-can--empty'
            )
          ) {
            return
          }

          const totalCents = getVariantSellingTotalForPackRow({
            otpCentsRaw: otpAttr,
            allocationsJson: allocAttr,
            variantFallback: variantPiniaFallback,
            selectedSellingPlan: plan,
            selectedSellingPlanIndex,
          })
          if (totalCents == null) return

          const perCanCents = Math.floor(totalCents / packCount)
          const money = productPrice(perCanCents)
          row.textContent = templateRaw
            ? formatPerCanLine(templateRaw, money)
            : money
        })
    },
  },
}
</script>
